/**
 * FormaFilesScreen — Forma "Data Management → Files" (#/forma).
 *
 * With APS auth: live ACC browse (hub → project → folders → .rvt files).
 * Without auth (dev bypass): falls back to mock demo rows.
 */

import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { isAuthDisabled } from "../../auth/apsConfig";
import { useAccBrowse } from "../../hooks/useAccBrowse";
import type { AccFileSelection } from "../../stores/accBrowseStore";
import { AccHubEmptyHelp } from "../AccHubEmptyHelp";
import { FormaLeftRail } from "./FormaLeftRail";
import { FORMA } from "./FormaTokens";
import { FormaTopBar } from "./FormaTopBar";

const VERA_ACCENT = "#6E56CF";

interface MockFileRow {
  projectId: string;
  fileName: string;
  version: string;
  size: string;
  updated: string;
  updatedBy: string;
  hub: string;
}

const MOCK_FILES: MockFileRow[] = [
  {
    projectId: "casita-blanca",
    fileName: "SampleADU_PA_ARCH_R26.rvt",
    version: "V4",
    size: "107 MB",
    updated: "May 31, 2026 10:26",
    updatedBy: "Alex Rivera",
    hub: "Vera Testing Hub",
  },
  {
    projectId: "snowdon-towers",
    fileName: "Sustainable_Villa_R24.rvt",
    version: "V3",
    size: "86 MB",
    updated: "Apr 18, 2026 09:12",
    updatedBy: "Jordan Lee",
    hub: "AOR — New York",
  },
];

export function FormaFilesScreen({
  onOpenVera,
}: {
  onOpenVera: (target: string | AccFileSelection) => void;
}) {
  if (isAuthDisabled()) {
    return <MockFormaFilesScreen onOpenVera={onOpenVera} />;
  }
  return <LiveAccFormaFilesScreen onOpenVera={onOpenVera} />;
}

function LiveAccFormaFilesScreen({
  onOpenVera,
}: {
  onOpenVera: (target: string | AccFileSelection) => void;
}) {
  const browse = useAccBrowse();

  const openVera = () => {
    if (browse.selection) onOpenVera(browse.selection);
  };

  return (
    <FormaShell>
      <HubProjectBar
        hubs={browse.hubs}
        projects={browse.projects}
        hubId={browse.hub?.id ?? ""}
        projectId={browse.project?.id ?? ""}
        region={browse.hub?.region}
        onHubChange={id => void browse.selectHub(id)}
        onProjectChange={id => void browse.selectProject(id)}
        onRefresh={() => void browse.loadHubs()}
      />

      {browse.error && (
        <p style={{ color: "#c0392b", margin: "0 0 12px", fontSize: 12 }}>
          {browse.error}
        </p>
      )}

      {!browse.loading && !browse.error && browse.hubs.length === 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 6,
            border: `1px solid ${FORMA.borderSoft}`,
            background: "#fff",
          }}
        >
          <AccHubEmptyHelp />
        </div>
      )}

      <div style={{ display: "flex", gap: 18, flex: 1, minHeight: 0 }}>
        <div style={{ width: 210, flexShrink: 0, overflowY: "auto" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: FORMA.textSecondary,
              marginBottom: 8,
              textTransform: "uppercase",
            }}
          >
            Folders
          </div>
          {browse.crumbs.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 11, lineHeight: 1.5 }}>
              {browse.crumbs.map((c, i) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => void browse.navigateToCrumb(i)}
                  style={{
                    display: "block",
                    background: "none",
                    border: "none",
                    padding: "2px 0",
                    font: "inherit",
                    fontSize: 11,
                    color: FORMA.blue,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {i > 0 ? "↳ " : ""}
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {browse.sidebarFolders.map(f => (
            <button
              type="button"
              key={f.id}
              onClick={() => void browse.openSidebarFolder(f)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 10px",
                marginBottom: 2,
                borderRadius: 4,
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: 13,
                textAlign: "left",
                background:
                  browse.activeFolderId === f.id
                    ? FORMA.blueSoftBg
                    : "transparent",
                color:
                  browse.activeFolderId === f.id
                    ? FORMA.blue
                    : FORMA.textPrimary,
                fontWeight: 500,
              }}
            >
              <span aria-hidden>🗀</span>
              {f.name}
            </button>
          ))}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Toolbar veraEnabled={!!browse.selection} onVera={openVera} />
          {browse.loading && browse.fileRows.length === 0 ? (
            <p style={{ color: FORMA.textSecondary }}>Loading files…</p>
          ) : (
            <LiveFileTable
              rows={browse.fileRows}
              selectedId={browse.selectedItemId}
              hubName={browse.hub?.name ?? ""}
              onSelect={id => browse.selectFile(id)}
            />
          )}
          <FooterCount
            selected={!!browse.selectedItemId}
            total={browse.fileRows.length}
            live
          />
        </div>
      </div>
    </FormaShell>
  );
}

function MockFormaFilesScreen({
  onOpenVera,
}: {
  onOpenVera: (target: string | AccFileSelection) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <FormaShell>
      <p
        style={{ fontSize: 12, color: FORMA.textSecondary, margin: "0 0 12px" }}
      >
        Offline demo — enable{" "}
        <code style={{ fontSize: 11 }}>VITE_ENABLE_APS_AUTH=true</code> for live
        ACC files.
      </p>
      <div style={{ display: "flex", gap: 18, flex: 1, minHeight: 0 }}>
        <div style={{ width: 190, flexShrink: 0 }}>
          <FolderItem label="Project Files" active />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Toolbar
            veraEnabled={!!selectedId}
            onVera={() => selectedId && onOpenVera(selectedId)}
          />
          <MockFileTable
            rows={MOCK_FILES}
            selectedId={selectedId}
            onSelect={id => setSelectedId(prev => (prev === id ? null : id))}
          />
          <FooterCount
            selected={!!selectedId}
            total={MOCK_FILES.length}
            live={false}
          />
        </div>
      </div>
    </FormaShell>
  );
}

function FormaShell({ children }: { children: ComponentChildren }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        background: FORMA.pageBg,
        color: FORMA.textPrimary,
        fontSize: 13,
      }}
    >
      <FormaTopBar />
      <main style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <FormaLeftRail />
        <section
          aria-label="Files"
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            padding: "20px 28px",
            overflow: "auto",
          }}
        >
          <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 16px" }}>
            Files
          </h1>
          <div
            style={{
              display: "flex",
              gap: 22,
              borderBottom: `1px solid ${FORMA.borderSoft}`,
              marginBottom: 16,
            }}
          >
            <Tab label="Folders" active />
            <Tab label="Packages" />
          </div>
          {children}
        </section>
      </main>
    </div>
  );
}

function HubProjectBar({
  hubs,
  projects,
  hubId,
  projectId,
  region,
  onHubChange,
  onProjectChange,
  onRefresh,
}: {
  hubs: { id: string; name: string }[];
  projects: { id: string; name: string }[];
  hubId: string;
  projectId: string;
  region?: string;
  onHubChange: (id: string) => void;
  onProjectChange: (id: string) => void;
  onRefresh: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        marginBottom: 14,
        fontSize: 12,
      }}
    >
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Hub
        <select
          value={hubId}
          onChange={e => onHubChange((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          {hubs.map(h => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Project
        <select
          value={projectId}
          onChange={e => onProjectChange((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          {projects.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {region && (
        <span style={{ color: FORMA.textSecondary }}>Region: {region}</span>
      )}
      <button type="button" onClick={onRefresh} style={ghostBtn}>
        Refresh
      </button>
    </div>
  );
}

function LiveFileTable({
  rows,
  selectedId,
  hubName,
  onSelect,
}: {
  rows: { itemId: string; fileName: string }[];
  selectedId: string | null;
  hubName: string;
  onSelect: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p style={{ color: FORMA.textSecondary, margin: 0 }}>
        No submission files (.rvt, .pdf) in this folder.
      </p>
    );
  }
  return (
    <div style={{ border: `1px solid ${FORMA.borderSoft}`, borderRadius: 6 }}>
      <div style={{ ...rowGrid, ...headerRow }}>
        <span />
        <span>Name</span>
        <span>Hub</span>
      </div>
      {rows.map(r => {
        const selected = r.itemId === selectedId;
        return (
          <button
            type="button"
            key={r.itemId}
            onClick={() => onSelect(r.itemId)}
            style={{
              ...rowGridLive,
              ...dataRow,
              background: selected ? FORMA.blueSoftBg : "#fff",
            }}
          >
            <input type="checkbox" checked={selected} readOnly aria-hidden />
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={rvtBadge}>R</span>
              <span style={{ fontWeight: 500, color: FORMA.textPrimary }}>
                {r.fileName}
              </span>
            </span>
            <span style={{ color: FORMA.textSecondary, fontSize: 12 }}>
              {hubName}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MockFileTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: MockFileRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div style={{ border: `1px solid ${FORMA.borderSoft}`, borderRadius: 6 }}>
      <div style={{ ...rowGrid, ...headerRow }}>
        <span />
        <span>Name</span>
        <span>Version</span>
        <span>Size</span>
        <span>Last updated</span>
        <span>Updated by</span>
      </div>
      {rows.map(r => {
        const selected = r.projectId === selectedId;
        return (
          <button
            type="button"
            key={r.projectId}
            onClick={() => onSelect(r.projectId)}
            style={{
              ...rowGrid,
              ...dataRow,
              background: selected ? FORMA.blueSoftBg : "#fff",
            }}
          >
            <input type="checkbox" checked={selected} readOnly aria-hidden />
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={rvtBadge}>R</span>
              <span style={{ fontWeight: 500 }}>{r.fileName}</span>
            </span>
            <span>
              <span style={versionPill}>{r.version}</span>
            </span>
            <span style={{ color: FORMA.textSecondary }}>{r.size}</span>
            <span style={{ color: FORMA.textSecondary }}>{r.updated}</span>
            <span style={{ color: FORMA.textSecondary }}>{r.updatedBy}</span>
          </button>
        );
      })}
    </div>
  );
}

function FooterCount({
  selected,
  total,
  live,
}: {
  selected: boolean;
  total: number;
  live: boolean;
}) {
  return (
    <div
      style={{
        marginTop: "auto",
        paddingTop: 12,
        fontSize: 12,
        color: FORMA.textSecondary,
        borderTop: `1px solid ${FORMA.borderSoft}`,
      }}
    >
      {selected ? `1 of ${total} selected` : `${total} items`}
      {live ? " (live ACC)" : " (demo)"}
      {selected ? (
        <span style={{ marginLeft: 10, color: VERA_ACCENT }}>
          — click <b>Vera</b> to run code compliance on this model
        </span>
      ) : (
        <span style={{ marginLeft: 10 }}>— select a model to enable Vera</span>
      )}
    </div>
  );
}

function Toolbar({
  veraEnabled,
  onVera,
}: {
  veraEnabled: boolean;
  onVera: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
      }}
    >
      <button type="button" style={uploadBtn}>
        ⬆ Upload files <span style={{ opacity: 0.85 }}>▾</span>
      </button>
      {veraEnabled && (
        <button type="button" onClick={onVera} style={veraBtn}>
          ✦ Check with Vera
        </button>
      )}
      <button type="button" style={ghostBtn}>
        ⇄ Move
      </button>
      <span style={{ marginLeft: "auto", color: "#9aa3af", fontSize: 12 }}>
        ⌕ Search and filter
      </span>
    </div>
  );
}

function Tab({ label, active }: { label: string; active?: boolean }) {
  return (
    <span
      style={{
        padding: "8px 2px",
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: active ? FORMA.textPrimary : FORMA.textSecondary,
        borderBottom: active
          ? `2px solid ${FORMA.blue}`
          : "2px solid transparent",
      }}
    >
      {label}
    </span>
  );
}

function FolderItem({ label, active }: { label: string; active?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 4,
        background: active ? FORMA.blueSoftBg : "transparent",
        color: active ? FORMA.blue : FORMA.textPrimary,
        fontWeight: 500,
      }}
    >
      <span aria-hidden>🗀</span>
      {label}
    </div>
  );
}

const selectStyle = {
  font: "inherit",
  fontSize: 12,
  padding: "6px 8px",
  borderRadius: 4,
  border: `1px solid ${FORMA.borderHard}`,
  minWidth: 160,
} as const;

const uploadBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: FORMA.blue,
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
} as const;

const veraBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: VERA_ACCENT,
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 6px rgba(110,86,207,0.4)",
} as const;

const ghostBtn = {
  background: "#fff",
  color: FORMA.textPrimary,
  border: `1px solid ${FORMA.borderHard}`,
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  cursor: "pointer",
} as const;

const rowGrid = {
  display: "grid",
  gridTemplateColumns: "34px 2.4fr 0.7fr 0.7fr 1.1fr 1.2fr",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  textAlign: "left",
  width: "100%",
} as const;

const rowGridLive = {
  display: "grid",
  gridTemplateColumns: "34px 2fr 1fr",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  textAlign: "left",
  width: "100%",
} as const;

const headerRow = {
  borderBottom: `1px solid ${FORMA.borderSoft}`,
  fontSize: 12,
  fontWeight: 600,
  color: FORMA.textSecondary,
  background: "#fafbfc",
} as const;

const dataRow = {
  border: "none",
  borderTop: `1px solid ${FORMA.borderSoft}`,
  cursor: "pointer",
  fontSize: 13,
  fontFamily: "inherit",
} as const;

const rvtBadge = {
  width: 22,
  height: 22,
  borderRadius: 3,
  background: FORMA.blue,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

const versionPill = {
  border: `1px solid ${FORMA.borderHard}`,
  borderRadius: 10,
  padding: "1px 9px",
  fontSize: 12,
  color: FORMA.textSecondary,
} as const;
